// JournalArticleForm migration: 2. XML export
//
// Deposit XML for journal articles is produced via the Metadata-Converter API (not
// client-side builders like Book). This file is the journal-specific
// XMLGenerationStrategy: it maps the new article/journal/issue form shape to the
// API payload (see map-data.ts), POSTs to convertApiEndpoint, then patches
// references into the returned XML (insert-citations.ts) before deposit.
import {
  ContentRegistrationType,
  RecordOrigin,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import { env } from '@/env';
import {
  XmlConversionBadInputError,
  XmlConversionError,
  XmlConversionNetworkError,
} from '@content-registration/errors';
import {
  EMAIL_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN,
  NAME_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN,
} from '@/constants/contact';
import { stripControlCharsInObject } from '@/xml/sanitize';
import { insertCitationsToXML } from '../builders/insert-citations';
import { mapJournalArticleData } from './map-data';

export class JournalArticleXmlRenderingStrategy
  implements XMLGenerationStrategy
{
  async generateXML(
    _recordType: ContentRegistrationType,
    data: FormData,
    _prettyPrint = false,
    userInfo?: UserInfo,
    origin?: RecordOrigin
  ): Promise<string> {
    const mappedFormData = mapJournalArticleData(
      data,
      origin?.resourceFieldsImported
    );
    const sanitizedFormData = stripControlCharsInObject(mappedFormData);
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    const raw = JSON.stringify({
      doiBatchHead: {
        depositorName:
          userInfo?.depositorName ||
          NAME_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN,
        depositorEmail:
          userInfo?.depositorEmail ||
          EMAIL_CONTENT_REGISTRATION_DEPOSITOR_UNKNOWN,
        registrant: 'content-registration-form',
      },
      ...sanitizedFormData,
    });
    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow' as RequestRedirect,
    };

    try {
      const response = await fetch(env().convertApiEndpoint, requestOptions);
      if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = JSON.stringify(errorResponse, null, 2);
        if (response.status === 400) {
          throw new XmlConversionBadInputError(`${response.statusText}`, {
            code: response.status,
            domainMessage: errorMessage,
          });
        }
        if (response.status === 500) {
          throw new XmlConversionError(response.statusText, {
            code: response.status,
          });
        }
        throw new Error('Unknown error');
      }
      const result = await response.text();
      const xmlWithCitations = insertCitationsToXML(
        result,
        sanitizedFormData?.journalArticle?.references
      );
      return xmlWithCitations;
    } catch (e) {
      if (e instanceof TypeError && e.message === 'Failed to fetch') {
        throw new XmlConversionNetworkError(e.message);
      }
      throw e;
    }
  }
}
